import { useMemo, useState } from 'react';
import GraphIcon from '../../../assets/icons/pulse-alert.svg?react';
import MachineIcon from '../../../assets/icons/machine-alarm.svg?react';
import { useGetMachinesAvailableSignalsQuery } from '../../../gql/generated';
import {
  formatMetricName,
  isMachineAlarmSignal,
} from '../../../utils/formatMetric';
import { Input } from '../../Input';
import { WarningBanner } from '../../WarningBanner';
import { SelectedSignal, signalKey } from './context';
import { useAddAlarmDialog } from './useAddAlarmDialog';
import { AlarmSignalUploadDropzone } from './AlarmSignalUploadDropzone';

interface SignalOption {
  key: string;
  signal: string;
  category: string;
  unit?: string | null;
  machineCount: number;
  totalMachines: number;
  machineIds: string[];
}

export function AlarmSignalStep() {
  const { formData, updateFormData, selectedMachineIds, availableMachines } =
    useAddAlarmDialog();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'alarm' | 'signal'>(
    'all'
  );
  const [activeTab, setActiveTab] = useState<'manual' | 'upload'>('manual');

  const { data: signalData, loading: signalsLoading } =
    useGetMachinesAvailableSignalsQuery({
      variables: {
        ids: selectedMachineIds,
      },
      skip: selectedMachineIds.length === 0,
    });

  const availableSignals = useMemo((): SignalOption[] => {
    if (!signalData?.machinesSignals?.rows) return [];

    const machines = signalData.machinesSignals.rows;
    if (machines.length === 0) return [];

    const signalsByKey = new Map<string, SignalOption>();
    const totalMachines = machines.length;

    machines.forEach((machine) => {
      machine.availableSignals?.signals?.forEach((category) => {
        category.signals?.forEach((signal) => {
          if (!signal.signal) return;

          const key = `${category.category}:${signal.signal}`;
          if (!signalsByKey.has(key)) {
            signalsByKey.set(key, {
              key,
              signal: signal.signal,
              category: category.category || 'General',
              unit: signal.unit,
              machineCount: 0,
              totalMachines,
              machineIds: [],
            });
          }

          const entry = signalsByKey.get(key);
          if (!entry) return;
          entry.machineCount += 1;
          entry.machineIds.push(machine.id);
        });
      });
    });

    return Array.from(signalsByKey.values()).sort((a, b) => {
      const categoryCompare = a.category.localeCompare(b.category);
      if (categoryCompare !== 0) return categoryCompare;
      return a.signal.localeCompare(b.signal);
    });
  }, [signalData]);

  const filteredSignals = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return availableSignals.filter((signal) => {
      const isMachineAlarm = isMachineAlarmSignal({
        unit: signal.unit,
        signalCategory: signal.category,
      });
      const matchesFilter =
        activeFilter === 'all' ||
        (activeFilter === 'alarm' && isMachineAlarm) ||
        (activeFilter === 'signal' && !isMachineAlarm);

      if (!matchesFilter) return false;
      if (!query) return true;

      const signalLabel = formatMetricName(signal.signal).toLowerCase();
      return (
        signalLabel.includes(query) ||
        signal.signal.toLowerCase().includes(query) ||
        signal.category.toLowerCase().includes(query)
      );
    });
  }, [availableSignals, activeFilter, searchQuery]);

  const topSignals = useMemo(
    () =>
      [...filteredSignals]
        .sort((a, b) => {
          if (a.machineCount !== b.machineCount) {
            return b.machineCount - a.machineCount;
          }
          return a.signal.localeCompare(b.signal);
        })
        .slice(0, 5),
    [filteredSignals]
  );

  const topSignalKeys = useMemo(
    () => new Set(topSignals.map((signal) => signal.key)),
    [topSignals]
  );

  const remainingSignals = useMemo(
    () => filteredSignals.filter((signal) => !topSignalKeys.has(signal.key)),
    [filteredSignals, topSignalKeys]
  );

  const selectedKeys = useMemo(
    () => new Set(formData.signals.map(signalKey)),
    [formData.signals]
  );

  // One banner line per selected signal that some selected machines lack
  // (168751). Rows are matched by signalKey, because two categories can share
  // a signal name.
  const partiallyAvailableSelections = useMemo(() => {
    const selectedMachines = availableMachines.filter((machine) =>
      formData.machines.includes(machine.id)
    );

    return formData.signals.flatMap((selected) => {
      const key = signalKey(selected);
      const signal = availableSignals.find((option) => option.key === key);
      if (!signal || signal.machineCount === signal.totalMachines) return [];

      const unavailableMachines = selectedMachines
        .filter((machine) => !signal.machineIds.includes(machine.id))
        .map((machine) => machine.displayName);

      return unavailableMachines.length > 0
        ? [{ key, signal: signal.signal, unavailableMachines }]
        : [];
    });
  }, [
    availableMachines,
    availableSignals,
    formData.machines,
    formData.signals,
  ]);

  const selectSignal = (signal: SignalOption) => {
    const key = signalKey(signal);
    const updatedSignals: SelectedSignal[] = selectedKeys.has(key)
      ? formData.signals.filter((selected) => signalKey(selected) !== key)
      : [
          ...formData.signals,
          {
            signal: signal.signal,
            category: signal.category,
            unit: signal.unit || '',
          },
        ];

    // category/unit must always describe signals[0], not whichever signal was
    // just clicked - that may no longer be (or never have been) the first signal.
    const primarySignal = updatedSignals[0];

    updateFormData({
      signals: updatedSignals,
      category: primarySignal ? primarySignal.category : formData.category,
      signalUnit: primarySignal ? primarySignal.unit : formData.signalUnit,
    });
  };

  const renderSignalRow = (signal: SignalOption) => {
    const isSelected = selectedKeys.has(signalKey(signal));
    const isMachineAlarm = isMachineAlarmSignal({
      unit: signal.unit,
      signalCategory: signal.category,
    });
    const hasPartialAvailability = signal.machineCount < signal.totalMachines;

    return (
      <button
        key={signal.key}
        type='button'
        aria-pressed={isSelected}
        onClick={() => selectSignal(signal)}
        className='hover:bg-surface-primary-support/50 flex w-full
          cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors
          outline-none focus-visible:ring-1 focus-visible:ring-blue-500
          focus-visible:ring-inset md:px-6'
      >
        <div className='pt-0.5'>
          <div
            className={`flex h-5 w-5 items-center justify-center rounded border
              ${
                isSelected
                  ? 'bg-focus border-focus'
                  : 'bg-surface-primary border-border-primary'
              }`}
          >
            {isSelected && (
              <svg
                className='text-surface-primary h-3 w-3'
                viewBox='0 0 16 16'
                fill='currentColor'
              >
                <path d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z' />
              </svg>
            )}
          </div>
        </div>

        <div className='flex min-w-0 flex-1 flex-col'>
          <span className='size-p2 text-primary font-bold'>
            {formatMetricName(signal.signal)}
          </span>
          <span className='size-p2 text-secondary'>
            {isMachineAlarm ? 'Machine alarm' : signal.category}
            {hasPartialAvailability ? ' - Unavailable for some machines' : ''}
          </span>
        </div>

        {isMachineAlarm ? (
          <MachineIcon className='text-primary mt-0.5 h-5 w-5 shrink-0' />
        ) : (
          <GraphIcon className='text-primary mt-0.5 h-5 w-5 shrink-0' />
        )}
      </button>
    );
  };

  return (
    <div className='flex flex-col'>
      <div className='bg-surface-primary border-border-secondary border-b'>
        <div className='px-4 pt-1.5 md:px-6'>
          <div className='flex gap-8'>
            <button
              type='button'
              onClick={() => setActiveTab('manual')}
              className={`relative -mb-px pb-3 transition-colors ${
                activeTab === 'manual'
                  ? `text-focus after:bg-focus after:absolute after:inset-x-0
                    after:-bottom-px after:h-0.5`
                  : 'text-secondary hover:text-primary'
                }`}
            >
              <span className='size-p2 font-bold'>Manual selection</span>
            </button>
            {/* TODO: Re-enable this code once the file upload has been fully worked out. */}
            {/*
            <button
              type='button'
              onClick={() => setActiveTab('upload')}
              className={`relative -mb-px pb-3 transition-colors ${
                activeTab === 'upload'
                  ? 'text-focus after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-focus'
                  : 'text-secondary hover:text-primary'
              }`}
            >
              <span className='size-p2 font-bold'>Upload file</span>
            </button>
            */}
          </div>
        </div>
      </div>

      {activeTab === 'manual' ? (
        <>
          <div
            className='bg-surface-primary flex flex-col gap-4 px-4 py-4 md:px-6'
          >
            <div className='flex flex-col gap-1.5'>
              <div className='relative flex w-full items-center'>
                <svg
                  className='text-secondary pointer-events-none absolute top-1/2
                    left-3 h-4 w-4 -translate-y-1/2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  aria-hidden='true'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
                <Input
                  id='signal-search'
                  aria-label='Search machine alarms or signals'
                  placeholder='Machine alarm or Signal'
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className='w-full pl-10'
                />
              </div>
            </div>

            <div className='flex flex-wrap gap-2'>
              <FilterChip
                label='All'
                isActive={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
              />
              <FilterChip
                label='Machine alarm'
                isActive={activeFilter === 'alarm'}
                onClick={() => setActiveFilter('alarm')}
              />
              <FilterChip
                label='Signal'
                isActive={activeFilter === 'signal'}
                onClick={() => setActiveFilter('signal')}
              />
            </div>
          </div>

          {signalsLoading && (
            <div className='flex items-center justify-center px-6 py-8'>
              <span className='size-p2 text-secondary'>Loading signals...</span>
            </div>
          )}

          {!signalsLoading && (
            <>
              {topSignals.length > 0 && (
                <>
                  <div
                    className='bg-surface-primary-support size-p2 text-secondary
                      px-4 py-2 font-bold md:px-6'
                  >
                    Most used signals
                  </div>
                  <div
                    className='border-border-secondary flex flex-col border-b'
                  >
                    {topSignals.map(renderSignalRow)}
                  </div>
                </>
              )}

              <div
                className='bg-surface-primary-support size-p2 text-secondary
                  px-4 py-2 font-bold md:px-6'
              >
                All signals
              </div>
              <div className='flex flex-col'>
                {remainingSignals.length === 0 && topSignals.length === 0 ? (
                  <div className='flex items-center justify-center py-8'>
                    <span className='size-p2 text-secondary'>
                      No signals found
                    </span>
                  </div>
                ) : (
                  remainingSignals.map(renderSignalRow)
                )}
              </div>

              {partiallyAvailableSelections.length > 0 && (
                <div className='px-4 pb-4 md:px-6'>
                  <WarningBanner>
                    {partiallyAvailableSelections.map(
                      ({ key, signal, unavailableMachines }, index) => (
                        <span
                          key={key}
                          className={index > 0 ? 'mt-2 block' : 'block'}
                        >
                          <span className='font-bold'>
                            {formatMetricName(signal)} is not available on all
                            selected machines
                          </span>
                          <br />
                          Excluded: {unavailableMachines.join(', ')}
                        </span>
                      )
                    )}
                  </WarningBanner>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <AlarmSignalUploadDropzone />
      )}
    </div>
  );
}

function FilterChip({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`border-border-primary rounded-full border px-4 py-2
        transition-colors ${
          isActive
            ? 'text-primary border-blue-200 bg-blue-100'
            : `bg-surface-primary text-secondary
              hover:bg-surface-primary-support`
        }`}
    >
      <span className='size-p2 font-bold'>{label}</span>
    </button>
  );
}
